import React, { useState } from 'react';
import Axios from 'axios';
import EntityManagement from './entityManagement';
import { Entity } from './entityForm';
import { mergeStateValue, deepClone } from '../utilities/generic';
import { FilterSelections } from '../reducers/filterSelections';
import { Groups } from '../reducers/authenticated/authenticatedReducer';
import { useTranslation } from 'react-i18next';

function EngineModelManagement(props: Readonly<{
    filterSelections: FilterSelections,
    groups: Groups,
    onDataUpdated: () => void,
}>) {

    interface EngineModel extends Entity {
        engine_manufacturer_id: number;
        engine_manufacturer_name: string;
    }

    const [engineManufacturers, setEngineManufacturers] = useState([]);
    const [engineModels, setEngineModels] = useState([] as EngineModel[]);
    const [currentEngineModel, setCurrentEngineModel] = useState<EngineModel>({ engine_manufacturer_id: 0, engine_manufacturer_name: '', id: 0, name: '' });

    const restPath = '/v10/engineModel/';

    const { t } = useTranslation();

    function fetchData() {
        Axios.get('/v10/engineManufacturer').then(response => setEngineManufacturers(response.data.result));
        Axios.get('/v10/engineModel').then(response => setEngineModels(response.data.result));
    }

    async function startEditing(id) {
        if (id === undefined) {
            setCurrentEngineModel({ engine_manufacturer_id: 0, engine_manufacturer_name: '', id: 0, name: '' });
        } else {
            const model = engineModels.find(e => e.id === id);
            if (!model) {
                throw new Error('Internal error');
            }
            setCurrentEngineModel(deepClone(model));
        }
    }

    function changeValue(name, value) {
        mergeStateValue(setCurrentEngineModel, name, value);
    }

    function validate() {
        if (currentEngineModel?.name?.trim() === "") {
            return t("Please specify an engine model name");
        } else if (!currentEngineModel?.engine_manufacturer_id) {
            return t("Please specify an engine manufacturer");
        }
        return "";
    }

    function getSanitizedEditingValues() {
        return { engine_manufacturer_id: currentEngineModel?.engine_manufacturer_id, name: currentEngineModel?.name?.trim() };
    }

    return (
        <EntityManagement
            groups={props.groups}
            filterSelections={props.filterSelections}
            onDataUpdated={props.onDataUpdated}

            restPath={restPath}
            fetchData={fetchData}

            editor={{
                edit: startEditing,
                getSanitizedValues: getSanitizedEditingValues,
                set: changeValue,
                validate: validate,
                entityName: 'currentEngineModel'
            }}

            texts={{
                localizedAddNewText: t('Add new engine model'),
                confirmDelete: t('Are you sure you want to delete the engine model?'),
                errors: {
                    generic: t("Engine model couldn't be saved! Please try again or contact administrator!"),
                    alreadyExists: t("An engine model with that name already exists for the given engine manufacturer"),
                    deleteInUse: t("Engine model is in use and cannot be deleted"),
                    deleteGeneric: t("Engine model couldn't be deleted! Please try again or contact administrator!")
                }
            }}

            entities={{ engineModels: engineModels, currentEngineModel: currentEngineModel, engineManufacturers: engineManufacturers }}
            entityName='engineModels'

            tableHeaders={[{ localizedText: t('Engine Manufacturer'), name: 'engine_manufacturer_name', sortable: true },
            { localizedText: t('Engine Name'), name: 'name', sortable: true },
            { localizedText: t('Edit'), type: 'edit' },
            { localizedText: t('Delete'), type: 'delete' }]}

            formFields={[{ label: t('Engine Manufacturer'), name: 'engine_manufacturer_id', allowedValues: { entityName: 'engineManufacturers', name: 'name' } },
            { label: t('Model Name'), name: 'name', placeholder: t('Enter an engine model name'), maxLength: 255 }]}
        />
    );
}

export default EngineModelManagement;
